import type { FunctionComponentWithRef } from "react";
import { graphql, useFragment } from "react-relay";

import { isArticleVideoAsHero } from "scmp-app/components/content/content-cover-video/helpers";
import { withHighlightedHeadline } from "scmp-app/components/content/content-item-render/highlight/hoc";
import { LinkHighlightArea } from "scmp-app/components/content/content-item-render/highlight/styles";
import { useContentItemProviders } from "scmp-app/components/content/content-item-render/hooks";
import type { ContentItemRenderVariantProps } from "scmp-app/components/content/content-item-render/types";
import type { homeSuperSplashLargePrimaryV2ContentItemContent$key } from "scmp-app/queries/__generated__/homeSuperSplashLargePrimaryV2ContentItemContent.graphql";

import {
  ActionBar,
  Container,
  Content,
  CoverImage,
  CoverImageStyledEntityLink,
  Headline,
  HeadlineContainer,
  LiveTagContainer,
  StyledEntityLink,
  SubHeadline,
} from "./styles";

export type Props = ContentItemRenderVariantProps & {
  reference: homeSuperSplashLargePrimaryV2ContentItemContent$key;
};

const Component: FunctionComponentWithRef<Props, HTMLDivElement> = ({
  className,
  onClick,
  ref,
  reference,
}) => {
  const content = useFragment(
    graphql`
      fragment homeSuperSplashLargePrimaryV2ContentItemContent on Content {
        entityId
        ...entityLink
        ...helpersIsArticleVideoAsHeroArticle
        ...hooksContentItemProvidersContent
          @arguments(
            withCoverImage: true
            withCoverImageSize540x360: true
            withCoverImageSize1200x800: true
            withCoverImageLeadingSlides: true
            withHeadline: true
            withLiveTag: true
            withSubHeadline: true
            withActionBar: true
            withVideoClickToPlay: true
          )
      }
    `,
    reference,
  );
  const providers = useContentItemProviders(content);

  const isPlayableVideo = isArticleVideoAsHero(content);

  return (
    <Container className={className} ref={ref}>
      {isPlayableVideo ? (
        <CoverImage $isVideo={isPlayableVideo}>
          {providers.videoClickToPlay({ ga4Position: "supersplash" })}
        </CoverImage>
      ) : (
        <CoverImageStyledEntityLink onClick={() => onClick?.(content.entityId)} reference={content}>
          <CoverImage $isVideo={isPlayableVideo}>
            {providers.coverImage({
              responsiveVariants: {
                desktopUp: "size1200x800",
                mobileUp: "size540x360",
                tabletUp: "size1200x800",
              },
              withCaption: false,
              withCarousel: true,
            })}
          </CoverImage>
        </CoverImageStyledEntityLink>
      )}
      <Content>
        <StyledEntityLink onClick={() => onClick?.(content.entityId)} reference={content}>
          <LinkHighlightArea>
            <HeadlineContainer>
              <LiveTagContainer>{providers.liveTag({ variant: "homeV2" })}</LiveTagContainer>
              <Headline>{providers.headline({ preferSocialHeadline: true })}</Headline>
            </HeadlineContainer>
            <SubHeadline>{providers.subHeadline()}</SubHeadline>
          </LinkHighlightArea>
        </StyledEntityLink>
        <ActionBar>
          {providers.actionBar({
            commentColorVariant: "light-action-blue",
            commentIconHeight: 14,
            commentIconWidth: 14,
            isBookmarkVisible: false,
            isCommentsVisible: true,
            isIconVisible: false,
          })}
        </ActionBar>
      </Content>
    </Container>
  );
};

Component.displayName = "ContentItemHomeSuperSplashLargePrimaryV2";

export const ContentItemHomeSuperSplashLargePrimaryV2 = withHighlightedHeadline(Component);
